/**
 * Statutory Exit Dashboard — PF Form 10 + ESI + Gratuity tracking.
 *
 * Route: /hr/statutory-exit
 *
 * For every separation, one row per applicable scheme. HR sees deadline
 * countdown + missing fields + mark-as-filed action. Form data preview
 * modal shows the exact fields to type into EPFO / ESIC portals.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Button, Card, Col, Descriptions, Form, Input, Modal, Row, Select,
  Space, Statistic, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd'
import {
  CheckCircleOutlined, ClockCircleOutlined, CopyOutlined, ExclamationCircleOutlined,
  FileExcelOutlined, FileTextOutlined, ReloadOutlined, WarningOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  statutoryExitApi, STATUS_COLOR, TYPE_COLOR, STATUTORY_STATUSES, STATUTORY_TYPES,
  type StatutoryExitRecord, type StatutoryStatus, type StatutoryType,
} from '@/features/hr/statutoryExitApi'
import { separationReportsApi, downloadBlob } from '@/features/hr/separationReportsApi'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

function daysUntil(iso: string | null): number | null {
  if (!iso) return null
  return dayjs(iso).startOf('day').diff(dayjs().startOf('day'), 'day')
}

export default function StatutoryExitDashboardPage() {
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<StatutoryStatus | 'OPEN' | undefined>('OPEN')
  const [typeFilter, setTypeFilter] = useState<StatutoryType | undefined>()
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [previewRec, setPreviewRec] = useState<StatutoryExitRecord | null>(null)
  const [filedRec, setFiledRec] = useState<StatutoryExitRecord | null>(null)
  const [filedForm] = Form.useForm()

  const summaryQ = useQuery({
    queryKey: ['statutory-summary'],
    queryFn: statutoryExitApi.summary,
    refetchInterval: 60_000,
  })

  const listQ = useQuery({
    queryKey: ['statutory-list', statusFilter, typeFilter, overdueOnly],
    queryFn: () => statutoryExitApi.list({
      status: statusFilter && statusFilter !== 'OPEN' ? statusFilter : undefined,
      statutory_type: typeFilter,
      overdue_only: overdueOnly,
      limit: 500,
    }),
  })

  // Client-side filter for "OPEN" pseudo-status
  const filtered = useMemo(() => {
    const all = listQ.data ?? []
    if (statusFilter === 'OPEN') {
      return all.filter((r) =>
        r.status === 'DATA_READY' || r.status === 'PENDING_INFO',
      )
    }
    return all
  }, [listQ.data, statusFilter])

  const markFiledMut = useMutation({
    mutationFn: (v: { id: string; filing_reference: string; remarks?: string }) =>
      statutoryExitApi.markFiled(v.id, {
        filing_reference: v.filing_reference, remarks: v.remarks,
      }),
    onSuccess: () => {
      message.success('Marked as filed')
      setFiledRec(null); filedForm.resetFields()
      qc.invalidateQueries({ queryKey: ['statutory-list'] })
      qc.invalidateQueries({ queryKey: ['statutory-summary'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Mark failed'),
  })

  const updateStatusMut = useMutation({
    mutationFn: (v: { id: string; new_status: StatutoryStatus; remarks?: string }) =>
      statutoryExitApi.updateStatus(v.id, { new_status: v.new_status, remarks: v.remarks }),
    onSuccess: () => {
      message.success('Status updated')
      qc.invalidateQueries({ queryKey: ['statutory-list'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Update failed'),
  })

  const handleExportEpfo = async () => {
    try {
      const res = await separationReportsApi.exportEpfoForm10('DATA_READY')
      downloadBlob(res, `EPFO_Form10_${dayjs().format('YYYY-MM-DD')}.xlsx`)
      message.success('EPFO Form 10 downloaded')
    } catch {
      message.error('Failed to export EPFO Form 10')
    }
  }
  const handleExportEsi = async () => {
    try {
      const res = await separationReportsApi.exportEsiExit('DATA_READY')
      downloadBlob(res, `ESI_Exit_${dayjs().format('YYYY-MM-DD')}.xlsx`)
      message.success('ESI Exit downloaded')
    } catch {
      message.error('Failed to export ESI Exit')
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 4 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>Statutory Exit — PF / ESI / Gratuity</Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<FileExcelOutlined />} onClick={handleExportEpfo}>
              EPFO Form 10 (Excel)
            </Button>
            <Button icon={<FileExcelOutlined />} onClick={handleExportEsi}>
              ESI Exit (Excel)
            </Button>
          </Space>
        </Col>
      </Row>
      <Text type="secondary">
        Track Form 10 (EPFO) + ESI exit updates + Gratuity eligibility for every separation.
        Records auto-generate on separation closure; HR fills the portals + marks filed here.
        Use the Excel buttons above to bulk-upload to EPFO / ESIC portals.
      </Text>

      {/* Summary cards */}
      <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Total" value={summaryQ.data?.total ?? '—'} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Pending"
              value={summaryQ.data?.pending ?? '—'}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Overdue"
              value={summaryQ.data?.overdue ?? 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<WarningOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Due ≤ 5 days"
              value={summaryQ.data?.due_within_5_days ?? 0}
              valueStyle={{ color: '#d4b106' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Filed"
              value={summaryQ.data?.filed ?? '—'}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="N/A"
              value={summaryQ.data?.not_applicable ?? 0} />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text>Status:</Text>
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 200 }}
            options={[
              { label: 'Open (Data ready + Pending info)', value: 'OPEN' },
              { label: '— All —', value: undefined },
              ...STATUTORY_STATUSES.map((s) => ({ label: s.replace(/_/g, ' '), value: s })),
            ]} />
          <Text>Type:</Text>
          <Select allowClear value={typeFilter} onChange={setTypeFilter}
            placeholder="All" style={{ width: 130 }}
            options={STATUTORY_TYPES.map((t) => ({ label: t, value: t }))} />
          <Space>
            <Switch checked={overdueOnly} onChange={setOverdueOnly} />
            <Text>Overdue only</Text>
          </Space>
          <Button icon={<ReloadOutlined />}
            onClick={() => qc.invalidateQueries({ queryKey: ['statutory-list'] })}>
            Refresh
          </Button>
        </Space>
      </Card>

      <Card>
        <Table<StatutoryExitRecord>
          dataSource={filtered}
          rowKey="id"
          loading={listQ.isLoading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          columns={[
            {
              title: 'Employee', key: 'emp', width: 240,
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{r.emp_name ?? '—'}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {r.employee_code} · {r.department_name ?? 'no dept'}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Scheme', dataIndex: 'statutory_type', width: 100,
              render: (v: StatutoryType) => <Tag color={TYPE_COLOR[v]}>{v}</Tag>,
            },
            {
              title: 'Exit Date', dataIndex: 'exit_date', width: 110,
              render: (v) => dayjs(v).format('DD MMM YY'),
            },
            {
              title: 'Reason', dataIndex: 'exit_reason_code', width: 130,
              render: (v) => v ? <Tag>{v}</Tag> : '—',
            },
            {
              title: 'Deadline', key: 'deadline', width: 150,
              render: (_, r) => {
                if (!r.statutory_deadline) return <Text type="secondary">—</Text>
                const days = daysUntil(r.statutory_deadline)
                if (r.status === 'FILED' || r.status === 'NOT_APPLICABLE') {
                  return <Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(r.statutory_deadline).format('DD MMM YY')}
                  </Text>
                }
                if (days === null) return '—'
                if (days < 0) return <Tag color="red">{Math.abs(days)} days overdue</Tag>
                if (days <= 2) return <Tag color="red">{days}d left</Tag>
                if (days <= 5) return <Tag color="orange">{days}d left</Tag>
                return <Text style={{ fontSize: 12 }}>{days}d left ({dayjs(r.statutory_deadline).format('DD MMM')})</Text>
              },
            },
            {
              title: 'Status', dataIndex: 'status', width: 160,
              render: (v: StatutoryStatus, r) => (
                <Space direction="vertical" size={0}>
                  <Tag color={STATUS_COLOR[v]}>{v.replace(/_/g, ' ')}</Tag>
                  {r.missing_fields.length > 0 && (
                    <Tooltip title={r.missing_fields.join(', ')}>
                      <Text type="danger" style={{ fontSize: 10 }}>
                        {r.missing_fields.length} field(s) missing
                      </Text>
                    </Tooltip>
                  )}
                </Space>
              ),
            },
            {
              title: 'Filing Ref', dataIndex: 'filing_reference', width: 160,
              render: (v) => v ? <Text code>{v}</Text> : <Text type="secondary">—</Text>,
            },
            {
              title: 'Action', key: 'action', fixed: 'right', width: 200,
              render: (_, r) => (
                <Space size={4}>
                  <Button size="small" icon={<FileTextOutlined />}
                    onClick={() => setPreviewRec(r)}>
                    Form Data
                  </Button>
                  {r.status !== 'FILED' && r.status !== 'NOT_APPLICABLE' && (
                    <Button size="small" type="primary"
                      onClick={() => { setFiledRec(r); filedForm.resetFields() }}>
                      Mark Filed
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Form Data preview modal */}
      <Modal
        open={!!previewRec}
        title={previewRec ? `${previewRec.statutory_type} Form Data — ${previewRec.emp_name}` : ''}
        onCancel={() => setPreviewRec(null)}
        width={700}
        footer={[
          <Button key="close" onClick={() => setPreviewRec(null)}>Close</Button>,
          previewRec && previewRec.status !== 'FILED' && (
            <Button key="filed" type="primary"
              onClick={() => {
                setFiledRec(previewRec)
                setPreviewRec(null)
                filedForm.resetFields()
              }}>
              Mark Filed
            </Button>
          ),
        ]}
      >
        {previewRec && (
          <>
            {previewRec.missing_fields.length > 0 && (
              <Alert
                type="warning" showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ marginBottom: 12 }}
                message={`${previewRec.missing_fields.length} required field(s) missing`}
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    {previewRec.missing_fields.map((f) => <li key={f}><Text code>{f}</Text></li>)}
                  </ul>
                }
              />
            )}
            <Alert
              type="info" showIcon
              style={{ marginBottom: 12 }}
              message="Copy these fields into the EPFO / ESIC portal"
              description={
                previewRec.statutory_type === 'PF' ?
                  "EPFO Form 10 (Member Departure Declaration). Click any value to copy." :
                previewRec.statutory_type === 'ESI' ?
                  "ESIC portal → Mark Worker Exit. Click any value to copy." :
                  "Gratuity estimate — manual computation required for filing."
              }
            />
            <Descriptions bordered size="small" column={1}>
              {previewRec.form_data && Object.entries(previewRec.form_data).map(([key, val]) => {
                const display = val === null || val === undefined ? '—'
                  : typeof val === 'object' ? JSON.stringify(val)
                  : String(val)
                return (
                  <Descriptions.Item key={key}
                    label={
                      <Text strong style={{ fontSize: 12 }}>
                        {key.replace(/_/g, ' ')}
                      </Text>
                    }>
                    <Paragraph
                      copyable={display !== '—' ? { text: display, icon: <CopyOutlined /> } : false}
                      style={{ marginBottom: 0 }}
                    >
                      {display}
                    </Paragraph>
                  </Descriptions.Item>
                )
              })}
            </Descriptions>
          </>
        )}
      </Modal>

      {/* Mark Filed modal */}
      <Modal
        open={!!filedRec}
        title={filedRec ? `Mark ${filedRec.statutory_type} as Filed — ${filedRec.emp_name}` : ''}
        onCancel={() => { setFiledRec(null); filedForm.resetFields() }}
        onOk={() => filedForm.validateFields().then((v) =>
          markFiledMut.mutate({ id: filedRec!.id, ...v })
        )}
        confirmLoading={markFiledMut.isPending}
        okText="Mark Filed"
      >
        {filedRec && (
          <Form form={filedForm} layout="vertical">
            <Form.Item
              name="filing_reference"
              label="Filing Reference"
              rules={[{ required: true, message: 'Required — e.g. EPFO ack number' }]}
              tooltip={
                filedRec.statutory_type === 'PF'
                  ? 'EPFO portal ack/challan number from Form 10 submission'
                  : 'ESIC portal acknowledgement number'
              }
            >
              <Input placeholder="e.g. EPFO-2026-XX-1234567" />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks">
              <TextArea rows={2} placeholder="Anything HR should remember about this filing" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  )
}
